import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import type { PresenceUser } from '@/lib/doc/types';
import { sanitizeUrl } from '@/lib/identity';
import { cn } from '@/lib/utils';

/** Overlapping avatars of users currently in the document. */
export function PresenceAvatars({
  users,
  max = 4,
  className,
}: {
  users: PresenceUser[];
  max?: number;
  className?: string;
}) {
  if (users.length === 0) return null;
  const shown = users.slice(0, max);
  const overflow = users.length - shown.length;

  return (
    <div className={cn('flex items-center -space-x-2', className)} aria-label={`${users.length} collaborators online`}>
      {shown.map((user) => {
        const picture = sanitizeUrl(user.picture);
        return (
          <Tooltip key={user.clientID}>
            <TooltipTrigger asChild>
              <span
                className="relative inline-flex rounded-full ring-2 ring-background"
                style={{ backgroundColor: user.color }}
              >
                <Avatar className="h-7 w-7 border border-background">
                  {picture ? (
                    <AvatarImage src={picture} alt={user.name} className="object-cover" />
                  ) : null}
                  <AvatarFallback
                    className="text-[10px] font-semibold text-white"
                    style={{ backgroundColor: user.color }}
                  >
                    {initials(user.name)}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background"
                  style={{ backgroundColor: user.color }}
                  aria-hidden
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-medium">{user.name}</p>
              <p className="text-xs text-muted-foreground">In this document</p>
            </TooltipContent>
          </Tooltip>
        );
      })}
      {overflow > 0 && (
        <span className="relative inline-flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-background">
          +{overflow}
        </span>
      )}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}
